import { useEffect, useState } from "react";

import { Combobox } from "@/components/combobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { useCreateTeam, useTeams } from "../api/use-admins";

interface TeamSelectProps {
  /** Tenant owning the teams — Upclaim only, defaults to the caller's tenant. */
  tenantId?: string;
  value: string | null;
  onChange: (teamId: string | null) => void;
  /** Pre-selects the first team, as the Vue user form did. */
  autoSelectFirst?: boolean;
  label?: string;
  /** One line under the control, for forms that explain what each field drives. */
  hint?: string;
}

/**
 * Team picker with inline creation. Renders nothing when the tenant has no team yet:
 * teams are optional, so an agency that doesn't use them never sees the field.
 */
export function TeamSelect({
  tenantId,
  value,
  onChange,
  autoSelectFirst = false,
  label = "Équipe",
  hint,
}: TeamSelectProps) {
  const [newTeamName, setNewTeamName] = useState<string | null>(null);

  const { data: teams = [], refetch } = useTeams(tenantId);
  const createTeam = useCreateTeam(tenantId);

  const isCreating = newTeamName !== null;

  useEffect(() => {
    if (autoSelectFirst && value === null && teams.length) {
      onChange(teams[0].id);
    }
  }, [autoSelectFirst, value, teams, onChange]);

  /** Creates the team, then selects it — the endpoint only returns `{ success }`. */
  async function submitNewTeam() {
    const name = newTeamName?.trim();
    if (!name) {
      return;
    }
    try {
      await createTeam.mutateAsync({ name });
      const { data } = await refetch();
      const created = data?.find((team) => team.name === name);
      if (created) {
        onChange(created.id);
      }
      setNewTeamName(null);
    } catch {
      // Error toast handled by notify.promise.
    }
  }

  if (!teams.length && !isCreating) {
    return null;
  }

  return (
    <div>
      <Label className="block text-sm font-medium text-gray-900">{label}</Label>
      {isCreating ? (
        <div className="mt-2 flex items-center gap-2">
          <Input
            value={newTeamName}
            onChange={(event) => setNewTeamName(event.currentTarget.value)}
            placeholder="Nom de la nouvelle équipe"
          />
          <Button
            type="button"
            variant="outline"
            disabled={!newTeamName.trim() || createTeam.isPending}
            onClick={() => void submitNewTeam()}
          >
            Créer
          </Button>
          <Button type="button" variant="ghost" onClick={() => setNewTeamName(null)}>
            Annuler
          </Button>
        </div>
      ) : (
        <Combobox
          className="mt-2 w-full"
          items={teams}
          value={teams.find((team) => team.id === value) ?? null}
          onChange={(team) => onChange(team?.id ?? null)}
          getKey={(team) => team.id}
          getLabel={(team) => team.name}
          placeholder="Aucune"
          unselectLabel="Aucune"
          searchPlaceholder="Rechercher une équipe..."
          emptyMessage="Aucune équipe trouvée"
          footerAction={{ label: "Créer une équipe…", onSelect: () => setNewTeamName("") }}
        />
      )}
      {hint ? <p className="mt-1.5 text-xs leading-relaxed text-gray-500">{hint}</p> : null}
    </div>
  );
}
